import React, { useState, useEffect } from 'react';
import { 
  Activity, 
  Search, 
  Filter, 
  Calendar,
  Download,
  RefreshCw,
  Clock,
  User,
  AlertCircle,
  CheckCircle,
  XCircle,
  Eye,
  EyeOff,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import toast from 'react-hot-toast';

const ActivityDashboard = ({ themeClasses }) => {
  const [activities, setActivities] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    page: 1,
    search: '',
    category: '',
    level: '',
    actor: '',
    success: '',
    dateFrom: '',
    dateTo: ''
  });
  const [pagination, setPagination] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [expandedActivity, setExpandedActivity] = useState(null);
  const [constants, setConstants] = useState(null);

  useEffect(() => {
    loadConstants();
    loadActivities();
    loadStats();
  }, []);

  useEffect(() => {
    loadActivities();
  }, [filters]);

  const loadConstants = async () => {
    try {
      const response = await fetch('/api/activities/constants', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setConstants(data);
      }
    } catch (error) {
      console.error('Failed to load constants:', error);
    }
  };

  const loadActivities = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });

      const response = await fetch(`/api/activities?${params}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

      if (response.ok) {
        const data = await response.json();
        setActivities(data.activities);
        setPagination(data.pagination);
      } else {
        toast.error('Failed to load activities');
      }
    } catch (error) {
      console.error('Failed to load activities:', error);
      toast.error('Failed to load activities');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await fetch('/api/activities/stats', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: 1 // Reset to first page when filtering
    }));
  };

  const clearFilters = () => {
    setFilters({
      page: 1,
      search: '',
      category: '',
      level: '',
      actor: '',
      success: '',
      dateFrom: '',
      dateTo: ''
    });
  };

  const exportActivities = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.dateFrom) params.append('dateFrom', filters.dateFrom);
      if (filters.dateTo) params.append('dateTo', filters.dateTo);

      const response = await fetch(`/api/activities/export/csv?${params}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `activities-${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success('Activities exported successfully');
      } else {
        toast.error('Failed to export activities');
      }
    } catch (error) {
      toast.error('Failed to export activities');
    }
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  const formatDuration = (duration) => {
    if (!duration) return '-';
    if (duration < 1000) return `${duration}ms`;
    return `${(duration / 1000).toFixed(2)}s`;
  };

  const getStatusIcon = (success) => {
    return success ? (
      <CheckCircle className="w-4 h-4 text-green-500" />
    ) : (
      <XCircle className="w-4 h-4 text-red-500" />
    );
  };

  const getLevelColor = (level) => {
    const colors = {
      debug: 'text-gray-500',
      info: 'text-blue-500',
      warn: 'text-yellow-500',
      error: 'text-red-500',
      critical: 'text-red-700'
    };
    return colors[level] || 'text-gray-500';
  };

  const getCategoryColor = (category) => {
    const colors = {
      user: 'bg-blue-100 text-blue-800',
      site: 'bg-green-100 text-green-800',
      page: 'bg-purple-100 text-purple-800',
      section: 'bg-orange-100 text-orange-800',
      ai: 'bg-pink-100 text-pink-800',
      image: 'bg-indigo-100 text-indigo-800',
      admin: 'bg-red-100 text-red-800',
      system: 'bg-gray-100 text-gray-800'
    };
    return colors[category] || 'bg-gray-100 text-gray-800';
  };

  if (loading && !activities.length) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className={`ml-3 ${themeClasses.text}`}>Loading activities...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Overview */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className={`${themeClasses.card} p-6`}>
            <div className="flex items-center">
              <Activity className="w-8 h-8 text-blue-500 mr-3" />
              <div>
                <p className={`text-sm ${themeClasses.textMuted}`}>Total Activities</p>
                <p className={`text-2xl font-bold ${themeClasses.heading}`}>{stats.overview.totalActivities}</p>
              </div>
            </div>
          </div>
          
          <div className={`${themeClasses.card} p-6`}>
            <div className="flex items-center">
              <Clock className="w-8 h-8 text-green-500 mr-3" />
              <div>
                <p className={`text-sm ${themeClasses.textMuted}`}>Today</p>
                <p className={`text-2xl font-bold ${themeClasses.heading}`}>{stats.overview.activitiesToday}</p>
              </div>
            </div>
          </div>
          
          <div className={`${themeClasses.card} p-6`}>
            <div className="flex items-center">
              <AlertCircle className="w-8 h-8 text-red-500 mr-3" />
              <div>
                <p className={`text-sm ${themeClasses.textMuted}`}>Errors</p>
                <p className={`text-2xl font-bold ${themeClasses.heading}`}>{stats.overview.errorActivities}</p>
              </div>
            </div>
          </div>
          
          <div className={`${themeClasses.card} p-6`}>
            <div className="flex items-center">
              <CheckCircle className="w-8 h-8 text-blue-500 mr-3" />
              <div>
                <p className={`text-sm ${themeClasses.textMuted}`}>Success Rate</p>
                <p className={`text-2xl font-bold ${themeClasses.heading}`}>{(100 - stats.overview.errorRate).toFixed(1)}%</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Activity Feed */}
      <div className={themeClasses.card}>
        <div className={`p-6 border-b ${themeClasses.border}`}>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className={`text-lg font-semibold ${themeClasses.heading}`}>Activity Feed</h3>
              <p className={themeClasses.text}>Real-time system activity monitoring</p>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`${themeClasses.buttonSecondary} flex items-center`}
              >
                <Filter className="w-4 h-4 mr-2" />
                Filters
                {showFilters ? <ChevronUp className="w-4 h-4 ml-1" /> : <ChevronDown className="w-4 h-4 ml-1" />}
              </button>
              <button
                onClick={loadActivities}
                className={`${themeClasses.buttonSecondary} flex items-center`}
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </button>
              <button
                onClick={exportActivities}
                className={`${themeClasses.button} flex items-center`}
              >
                <Download className="w-4 h-4 mr-2" />
                Export
              </button>
            </div>
          </div>

          {/* Filters Panel */}
          {showFilters && (
            <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
                {/* Search */}
                <div className="lg:col-span-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      type="text"
                      placeholder="Search activities..."
                      value={filters.search}
                      onChange={(e) => handleFilterChange('search', e.target.value)}
                      className={`w-full pl-10 ${themeClasses.input}`}
                    />
                  </div>
                </div>

                {/* Category Filter */}
                <div>
                  <select
                    value={filters.category}
                    onChange={(e) => handleFilterChange('category', e.target.value)}
                    className={`w-full ${themeClasses.input}`}
                  >
                    <option value="">All Categories</option>
                    {constants?.categories.map(category => (
                      <option key={category} value={category}>
                        {category.charAt(0).toUpperCase() + category.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Level Filter */}
                <div>
                  <select
                    value={filters.level}
                    onChange={(e) => handleFilterChange('level', e.target.value)}
                    className={`w-full ${themeClasses.input}`}
                  >
                    <option value="">All Levels</option>
                    {constants?.levels.map(level => (
                      <option key={level} value={level}>
                        {level.charAt(0).toUpperCase() + level.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Actor Filter */}
                <div>
                  <select
                    value={filters.actor}
                    onChange={(e) => handleFilterChange('actor', e.target.value)}
                    className={`w-full ${themeClasses.input}`}
                  >
                    <option value="">All Actors</option>
                    {constants?.actorTypes.map(actor => (
                      <option key={actor} value={actor}>
                        {actor.charAt(0).toUpperCase() + actor.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Success Filter */}
                <div>
                  <select
                    value={filters.success}
                    onChange={(e) => handleFilterChange('success', e.target.value)}
                    className={`w-full ${themeClasses.input}`}
                  >
                    <option value="">All Results</option>
                    <option value="true">Success Only</option>
                    <option value="false">Errors Only</option>
                  </select>
                </div>
              </div>

              {/* Date Range */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={`block text-sm font-medium ${themeClasses.label}`}>From Date</label>
                  <input
                    type="datetime-local"
                    value={filters.dateFrom}
                    onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                    className={`w-full ${themeClasses.input}`}
                  />
                </div>
                <div>
                  <label className={`block text-sm font-medium ${themeClasses.label}`}>To Date</label>
                  <input
                    type="datetime-local"
                    value={filters.dateTo}
                    onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                    className={`w-full ${themeClasses.input}`}
                  />
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  onClick={clearFilters}
                  className={themeClasses.buttonSecondary}
                >
                  Clear All Filters
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Activities List */}
        <div className="divide-y divide-gray-200">
          {activities.map((activity) => (
            <div key={activity._id} className={`p-6 ${themeClasses.hover}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-4 flex-1">
                  <div className="flex-shrink-0">
                    {getStatusIcon(activity.details.success)}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2 mb-2">
                      <span className={`inline-flex px-2 py-1 text-xs rounded-full ${getCategoryColor(activity.category)}`}>
                        {activity.category}
                      </span>
                      <span className={`text-sm font-medium ${getLevelColor(activity.level)}`}>
                        {activity.level}
                      </span>
                      <span className={`text-sm ${themeClasses.textMuted}`}>
                        {formatTimestamp(activity.timestamp)}
                      </span>
                    </div>
                    
                    <p className={`text-sm font-medium ${themeClasses.heading} mb-1`}>
                      {activity.action.replace(/\./g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </p>
                    
                    <div className="flex items-center space-x-4 text-sm text-gray-500">
                      {activity.actor.name && (
                        <span className="flex items-center">
                          <User className="w-3 h-3 mr-1" />
                          {activity.actor.name}
                        </span>
                      )}
                      {activity.target.name && (
                        <span>→ {activity.target.name}</span>
                      )}
                      {activity.details.duration && (
                        <span className="flex items-center">
                          <Clock className="w-3 h-3 mr-1" />
                          {formatDuration(activity.details.duration)}
                        </span>
                      )}
                    </div>
                    
                    {activity.details.error && (
                      <p className="text-sm text-red-600 mt-2">
                        Error: {activity.details.error}
                      </p>
                    )}
                  </div>
                </div>
                
                <button
                  onClick={() => setExpandedActivity(expandedActivity === activity._id ? null : activity._id)}
                  className={themeClasses.iconButton}
                >
                  {expandedActivity === activity._id ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              
              {/* Expanded Details */}
              {expandedActivity === activity._id && (
                <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <pre className="text-xs text-gray-600 dark:text-gray-400 overflow-x-auto whitespace-pre-wrap">
                    {JSON.stringify(activity, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Pagination */}
        {pagination && pagination.totalPages > 1 && (
          <div className={`px-6 py-4 border-t ${themeClasses.border} flex items-center justify-between`}>
            <div className={`text-sm ${themeClasses.textMuted}`}>
              Showing {((pagination.currentPage - 1) * 50) + 1} to {Math.min(pagination.currentPage * 50, pagination.totalActivities)} of {pagination.totalActivities} activities
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => handleFilterChange('page', Math.max(1, filters.page - 1))}
                disabled={!pagination.hasPrev}
                className={`px-3 py-1 text-sm border rounded ${!pagination.hasPrev ? 'opacity-50 cursor-not-allowed' : themeClasses.buttonSecondary}`}
              >
                Previous
              </button>
              <span className={`text-sm ${themeClasses.text}`}>
                Page {pagination.currentPage} of {pagination.totalPages}
              </span>
              <button
                onClick={() => handleFilterChange('page', Math.min(pagination.totalPages, filters.page + 1))}
                disabled={!pagination.hasNext}
                className={`px-3 py-1 text-sm border rounded ${!pagination.hasNext ? 'opacity-50 cursor-not-allowed' : themeClasses.buttonSecondary}`}
              >
                Next
              </button>
            </div>
          </div>
        )}

        {/* Empty State */}
        {!loading && activities.length === 0 && (
          <div className="text-center py-12">
            <Activity className={`w-12 h-12 ${themeClasses.textMuted} mx-auto mb-4`} />
            <p className={themeClasses.textMuted}>
              {Object.values(filters).some(v => v && v !== 1) ? 'No activities match your filters' : 'No activities found'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ActivityDashboard;